import React from 'react';
import WikiIcon from '../../shared/WikiIcon';
import { REJECT_REASONS, barWidth, pctLabel } from './brainOpsShared';

/**
 * One pending proposal in the Review queue (E2 §1.2): kind chip + fresh chip +
 * where, title, plain explanation, the 4-step pipeline, EVIDENCE + REPLAY TEST
 * grid, the drafted wording (a textarea while editing), the risk line and the
 * footer band (decision controls for Manage AI Brain, a lock line otherwise).
 *
 * Pure: every action is a callback from BrainReviewTab.
 */

const KINDS = Object.freeze({
    guardrail: { label: 'New guardrail', className: 'wk-bro-pill--violet' },
    replace: { label: 'Rewrite a check', className: 'wk-bro-pill--sky' },
    graft: { label: 'New check', className: 'wk-bro-pill--graft' },
});

/** Prototype `kindStyle`: guardrail violet, replace sky, graft (and anything else) teal. */
export function proposalKind(proposal) {
    const kind = KINDS[proposal?.kind] || KINDS.graft;
    return { label: proposal?.kind_label || kind.label, className: kind.className };
}

/**
 * `passed` | `failed` | `not_tested`. The contract reports a proposal the
 * harness could not test as `passed:false` + `replay.state:"not_tested"`; the
 * prototype only knows passed/failed, so "not tested" gets its own honest
 * state instead of being shown as a failure.
 */
export function proposalReplayState(proposal) {
    const state = proposal?.replay?.state;
    if (state === 'not_tested') return 'not_tested';
    if (proposal?.passed) return 'passed';
    if (!proposal?.replay && (proposal?.own_pct === null || proposal?.own_pct === undefined)) return 'not_tested';
    return 'failed';
}

function pipelineSteps(proposal, replayState, replaying) {
    const replaySub = replaying
        ? 'Replay test running'
        : { passed: 'Passed', failed: 'Failed', not_tested: 'Not tested yet' }[replayState];
    const replayTone = replaying || replayState === 'not_tested'
        ? 'waiting'
        : (replayState === 'passed' ? 'done' : 'failed');
    return [
        { id: 'spotted', label: 'Spotted', sub: proposal?.spotted || '', tone: 'done' },
        { id: 'drafted', label: 'Drafted by AI', sub: 'Wording below', tone: 'done' },
        { id: 'replay', label: 'Replay test', sub: replaySub, tone: replayTone },
        { id: 'decision', label: 'Your decision', sub: 'Waiting', tone: 'waiting' },
    ];
}

const STEP_ICONS = { done: 'check', failed: 'x', waiting: 'clock' };

function ReplayMeter({ label, pct, ok, caption, testId }) {
    return (
        <div className="wk-brq-meter" data-testid={testId}>
            <div className="wk-brq-meter-row">
                <span>{label}</span>
                <b className="wk-t-num">{pctLabel(pct)}</b>
            </div>
            <div className="wk-brq-track">
                <div
                    className={`wk-brq-fill${ok ? '' : ' wk-brq-fill--bad'}`}
                    style={{ width: `${barWidth(pct)}%` }}
                    data-testid={testId ? `${testId}-fill` : undefined}
                />
            </div>
            {caption && <div className="wk-brq-meter-n">{caption}</div>}
        </div>
    );
}

export default function ProposalCard({
    proposal,
    canManage = false,
    readOnly = true,
    draftEdit = null,
    rejectOpen = false,
    busy = null,
    onStartEdit,
    onEditChange,
    onOpenReject,
    onCancelReject,
    onReject,
    onApprove,
    onReplay,
}) {
    const id = proposal.proposal_id || proposal.id;
    const kind = proposalKind(proposal);
    const replayState = proposalReplayState(proposal);
    const passed = replayState === 'passed';
    const editing = draftEdit !== null && draftEdit !== undefined;
    const steps = pipelineSteps(proposal, replayState, busy === 'replay');
    const evidence = Array.isArray(proposal.evidence) ? proposal.evidence : [];
    const ownPct = proposal.own_pct ?? proposal.replay?.own?.pct ?? null;
    const ctlPct = proposal.ctl_pct ?? proposal.replay?.control?.pct ?? null;
    const ownOk = ownPct !== null && Number(ownPct) >= (proposal.replay?.thresholds?.own_min ?? 80);
    const ctlOk = ctlPct !== null && Number(ctlPct) <= (proposal.replay?.thresholds?.control_max ?? 20);
    const isBusy = Boolean(busy);
    const approveDisabled = !passed || isBusy || (editing && !String(draftEdit).trim());

    let footerMessage;
    if (replayState === 'passed') {
        footerMessage = (
            <span className="wk-brq-footer-msg">
                Approving switches it on now. You can turn it off from the tree at any time.
            </span>
        );
    } else if (replayState === 'failed') {
        footerMessage = (
            <span className="wk-brq-footer-msg wk-brq-footer-msg--failed">
                Can't be approved: it failed the replay test.
            </span>
        );
    } else {
        footerMessage = (
            <span className="wk-brq-footer-msg">
                Can't be approved until it passes the replay test.
            </span>
        );
    }

    return (
        <article
            className="wk-brq-card"
            data-testid="brain-proposal"
            data-proposal-id={id}
            data-replay={replayState}
            aria-busy={isBusy || undefined}
        >
            <div className="wk-brq-top">
                <div className="wk-brq-chips">
                    <span className={`wk-pill ${kind.className}`} data-testid="proposal-kind">{kind.label}</span>
                    {proposal.fresh && (
                        <span className="wk-pill wk-pill--amber" data-testid="proposal-fresh">From your feedback just now</span>
                    )}
                    {proposal.where && <span className="wk-t-meta" data-testid="proposal-where">{proposal.where}</span>}
                </div>
                <h3 className="wk-t-h wk-brq-title">{proposal.title}</h3>
                {proposal.plain && <p className="wk-t-body wk-brq-plain">{proposal.plain}</p>}
                <ol className="wk-brq-steps" aria-label="Proposal progress">
                    {steps.map((step) => (
                        <li key={step.id} className="wk-brq-step" data-step={step.id} data-tone={step.tone}>
                            <span className={`wk-brq-dot wk-brq-dot--${step.tone}`} aria-hidden="true">
                                <WikiIcon name={STEP_ICONS[step.tone]} size={11} strokeWidth={3} />
                            </span>
                            <span className="wk-brq-step-text">
                                <span className="wk-brq-step-label">{step.label}</span>
                                <span className="wk-t-meta wk-brq-step-sub">{step.sub}</span>
                            </span>
                        </li>
                    ))}
                </ol>
            </div>

            <div className="wk-brq-grid">
                <section className="wk-brq-col" aria-label="Evidence">
                    <span className="wk-t-label wk-bro-label">Evidence</span>
                    {evidence.map((item, index) => (
                        <div key={item.turn_id || `${item.when}-${index}`} className="wk-brq-ev" data-testid="proposal-evidence">
                            <span className="wk-brq-ev-when">{item.when}</span>
                            <span className="wk-brq-ev-text">{item.text}</span>
                        </div>
                    ))}
                </section>
                <section className="wk-brq-col" aria-label="Replay test">
                    <span className="wk-t-label wk-bro-label wk-brq-label-replay">Replay test</span>
                    <ReplayMeter
                        testId="proposal-own"
                        label="Catches its own cases (needs 80%+)"
                        pct={ownPct}
                        ok={ownOk}
                        caption={proposal.own_n || proposal.replay?.own_n_label}
                    />
                    <ReplayMeter
                        testId="proposal-ctl"
                        label="Fires on unrelated questions (20% max)"
                        pct={ctlPct}
                        ok={ctlOk}
                        caption={proposal.ctl_n || proposal.replay?.control_n_label}
                    />
                </section>
            </div>

            <div className="wk-brq-wording">
                <span className="wk-t-label wk-bro-label">Wording drafted by the AI</span>
                {editing ? (
                    <textarea
                        className="wk-brq-textarea"
                        value={draftEdit}
                        onChange={(event) => onEditChange && onEditChange(id, event.target.value)}
                        aria-label="Wording drafted by the AI"
                        data-testid="proposal-wording-input"
                        disabled={isBusy}
                    />
                ) : (
                    <div className="wk-brq-draft" data-testid="proposal-wording">{proposal.draft}</div>
                )}
                {proposal.risk && (
                    <div className="wk-brq-risk" data-testid="proposal-risk">
                        <WikiIcon name="alert-triangle" size={13} />
                        <span>Risk: {proposal.risk}</span>
                    </div>
                )}
            </div>

            <div className="wk-brq-footer" data-testid="proposal-footer">
                {footerMessage}
                {readOnly && (
                    <span className="wk-brq-lock" data-testid="proposal-locked">
                        <WikiIcon name="lock" size={12} />
                        Only people with Manage AI Brain can decide
                    </span>
                )}
                {canManage && rejectOpen && (
                    <div className="wk-brq-reject" role="group" aria-label="Why reject it?">
                        <span className="wk-brq-why">Why?</span>
                        {REJECT_REASONS.map((reason) => (
                            <button
                                key={reason}
                                type="button"
                                className="wk-brq-reason"
                                onClick={() => onReject && onReject(proposal, reason)}
                                disabled={isBusy}
                                data-testid="proposal-reject-reason"
                            >
                                {reason}
                            </button>
                        ))}
                        <button
                            type="button"
                            className="wk-brq-cancel"
                            onClick={() => onCancelReject && onCancelReject(id)}
                            disabled={isBusy}
                        >
                            Cancel
                        </button>
                    </div>
                )}
                {canManage && !rejectOpen && (
                    <div className="wk-brq-actions">
                        <button
                            type="button"
                            className="wk-btn wk-btn--secondary wk-brq-btn"
                            onClick={() => onOpenReject && onOpenReject(id)}
                            disabled={isBusy}
                        >
                            Reject
                        </button>
                        {replayState === 'not_tested' && (
                            <button
                                type="button"
                                className="wk-btn wk-btn--secondary wk-brq-btn"
                                onClick={() => onReplay && onReplay(proposal)}
                                disabled={isBusy}
                                data-testid="proposal-replay"
                            >
                                <WikiIcon name="play" size={13} />
                                Run replay test
                            </button>
                        )}
                        {passed && !editing && (
                            <button
                                type="button"
                                className="wk-btn wk-btn--secondary wk-brq-btn"
                                onClick={() => onStartEdit && onStartEdit(proposal)}
                                disabled={isBusy}
                            >
                                <WikiIcon name="pencil" size={13} />
                                Edit wording
                            </button>
                        )}
                        <button
                            type="button"
                            className={`wk-btn wk-btn--teal wk-brq-btn wk-brq-approve${passed ? '' : ' is-blocked'}`}
                            onClick={() => { if (!approveDisabled && onApprove) onApprove(proposal); }}
                            disabled={approveDisabled}
                            aria-busy={busy === 'accept' || undefined}
                            data-testid="proposal-approve"
                        >
                            <WikiIcon name="check" size={15} />
                            {editing ? 'Save wording and approve' : 'Approve'}
                        </button>
                    </div>
                )}
            </div>
        </article>
    );
}
